import { useCallback, useEffect } from 'react';
import { telemetryService } from '../api';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('useTelemetry');
import React from 'react';

interface UseTelemetryReturn {
  trackEvent: (name: string, attributes?: Record<string, any>) => void;
  trackUserInteraction: (action: string, target: string, attributes?: Record<string, any>) => void;
  trackNavigation: (from: string, to: string, attributes?: Record<string, any>) => void;
  trackError: (error: Error, context?: string, attributes?: Record<string, any>) => void;
  getCurrentTraceId: () => string | null;
}

/**
 * React hook for telemetry tracking
 */
export const useTelemetry = (componentName?: string): UseTelemetryReturn => {
  
  // Track component mount/unmount
  useEffect(() => {
    if (componentName) {
      try {
        telemetryService.trackEvent('Component.Mount', {
          'component.name': componentName,
          'component.timestamp': Date.now(),
        });
      } catch (error) {
        logger.warn('Failed to track component mount:', error);
      }
    }

    return () => {
      if (componentName) {
        try {
          telemetryService.trackEvent('Component.Unmount', {
            'component.name': componentName,
            'component.timestamp': Date.now(),
          });
        } catch (error) {
          logger.warn('Failed to track component unmount:', error);
        }
      }
    };
  }, [componentName]);

  const trackEvent = useCallback((name: string, attributes?: Record<string, any>) => {
    try {
      telemetryService.trackEvent(name, {
        'component.name': componentName,
        ...attributes,
      });
    } catch (error) {
      logger.warn('Failed to track event:', error);
    }
  }, [componentName]);

  const trackUserInteraction = useCallback((action: string, target: string, attributes?: Record<string, any>) => {
    try {
      telemetryService.trackUserInteraction(action, target, {
        'component.name': componentName,
        ...attributes,
      });
    } catch (error) {
      logger.warn('Failed to track user interaction:', error);
    }
  }, [componentName]);

  const trackNavigation = useCallback((from: string, to: string, attributes?: Record<string, any>) => {
    try {
      telemetryService.trackNavigation(from, to, {
        'component.name': componentName,
        ...attributes,
      });
    } catch (error) {
      logger.warn('Failed to track navigation:', error);
    }
  }, [componentName]);

  const trackError = useCallback((error: Error, context?: string, attributes?: Record<string, any>) => {
    try {
      telemetryService.trackError(error, context || componentName, {
        'component.name': componentName,
        ...attributes,
      });
    } catch (telemetryError) {
      logger.warn('Failed to track error:', telemetryError);
    }
  }, [componentName]);

  const getCurrentTraceId = useCallback(() => {
    try {
      return telemetryService.getCurrentTraceId();
    } catch (error) {
      logger.warn('Failed to get trace ID:', error);
      return null;
    }
  }, []);

  return {
    trackEvent,
    trackUserInteraction,
    trackNavigation,
    trackError,
    getCurrentTraceId,
  };
};

/**
 * Higher-order component for automatic telemetry tracking
 */
export const withTelemetry = <P extends object>(
  Component: React.ComponentType<P>,
  componentName?: string
) => {
  const WrappedComponent = (props: P) => {
    const { trackEvent } = useTelemetry(componentName || Component.name);

    useEffect(() => {
      trackEvent('Component.Render');
    });

    return React.createElement(Component, props);
  };

  WrappedComponent.displayName = `withTelemetry(${componentName || Component.name})`;
  return WrappedComponent;
}; 